import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck, Sparkles, Users } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { ConversionBand } from "@/components/site/ConversionBand";
import { Reveal } from "@/hooks/useReveal";
import { trackCTA, trackConversion } from "@/lib/tracking";
import { buildInquireHref, inquireContextProps, readInquireContext } from "@/lib/inquire-link";
import { useHeroFormStart } from "@/lib/hero-analytics";

const TRUST = [
  { icon: ShieldCheck, label: "Selective intake" },
  { icon: Sparkles, label: "Strategy, not sales" },
  { icon: Users, label: "1 business-day response" },
];

const LANES = [
  { value: "coaching", label: "1:1 Executive Coaching" },
  { value: "speaking", label: "Keynote Speaking" },
  { value: "consulting", label: "Org Consulting" },
  { value: "masterclass", label: "Masterclass / Cohort" },
];

const inquirySchema = z.object({
  lane: z.string().min(1).max(64),
  full_name: z.string().trim().min(2, "Name required").max(100),
  email: z.string().trim().email("Valid email required").max(255),
  phone: z.string().trim().max(32).optional().or(z.literal("")),
  organization: z.string().trim().max(150).optional().or(z.literal("")),
  role: z.string().trim().max(100).optional().or(z.literal("")),
  event_or_focus: z.string().trim().max(500).optional().or(z.literal("")),
  date_or_timeline: z.string().trim().max(200).optional().or(z.literal("")),
  budget_range: z.string().trim().max(100).optional().or(z.literal("")),
  message: z.string().trim().min(10, "Tell us a bit more (10+ chars)").max(2000),
});

const inputCls =
  "bg-navy-elevated/60 border-border text-white placeholder:text-silver/50 focus-visible:ring-2 focus-visible:ring-gold/60 focus-visible:border-gold/60 transition-colors";

const Strategy = () => {
  const [params] = useSearchParams();
  const initialLane = params.get("lane") ?? "coaching";
  const p = (key: string, max: number) =>
    (params.get(key) ?? "").trim().slice(0, max);

  const prefill = {
    full_name: p("name", 100),
    email: p("email", 255),
    phone: p("phone", 32),
    organization: p("organization", 150) || p("org_type", 150),
    role: p("role", 100),
    tier: p("tier", 100),
    event_or_focus: p("event_or_focus", 500) || p("focus", 500),
    date_or_timeline: p("date_or_timeline", 200) || p("timeline", 200),
    budget_range: p("budget_range", 100),
    message: p("message", 2000),
  };
  const isTierInquiry = params.get("intent") === "tier-inquiry" && !!prefill.tier;

  /**
   * Attribution context propagated by the CTA that sent the visitor here
   * (lane + tier + source + intent + any UTM / click ids). Stamped onto every
   * analytics event on this page — view, submit attempt, and confirmation —
   * so the funnel is consistent end-to-end.
   */
  const inquireCtx = useMemo(() => readInquireContext(params), [params]);
  const ctxProps = useMemo(() => inquireContextProps(inquireCtx), [inquireCtx]);



  const heroFormStart = useHeroFormStart("strategy_inquire");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const startedAt = useRef<number>(Date.now());

  const [form, setForm] = useState({
    lane: LANES.find((l) => l.value === initialLane)?.value ?? "coaching",
    full_name: prefill.full_name,
    email: prefill.email,
    phone: prefill.phone,
    organization: prefill.organization,
    role: prefill.role,
    event_or_focus:
      prefill.event_or_focus ||
      (isTierInquiry ? `FSAP retainer — ${prefill.tier} tier` : ""),
    date_or_timeline: prefill.date_or_timeline,
    budget_range: prefill.budget_range || (isTierInquiry ? prefill.tier : ""),
    message: prefill.message,
    website: "", // honeypot
  });

  const prefilledCount = Object.values(prefill).filter(Boolean).length;

  // One view event per unique inbound context.
  useEffect(() => {
    trackCTA("inquire_view", { surface: "strategy_form", lane: form.lane, ...ctxProps });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxProps]);





  const update = (k: keyof typeof form) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((s) => ({ ...s, [k]: e.target.value }));
      if (errors[k as string]) {
        setErrors((prev) => {
          const n = { ...prev };
          delete n[k as string];
          return n;
        });
      }
    };

  const validateField = (field: keyof typeof form) => {
    const r = inquirySchema.safeParse(form);
    setErrors((prev) => {
      const next = { ...prev };
      const issue = r.success ? undefined : r.error.issues.find((i) => i.path[0] === field);
      if (issue) next[field as string] = issue.message;
      else delete next[field as string];
      return next;
    });
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Fire an intent-level CTA event on every submit *attempt* (before
    // validation). Lets us measure abandonment vs. successful conversion
    // — the ratio between `strategy_session_submit_click` and
    // `strategy_session_submit` is the form's validation-drop rate.
    trackCTA("strategy_session_submit_click", {
      lane: form.lane,
      surface: "strategy_form",
      ...ctxProps,
    });
    if (form.website) return; // honeypot
    if (Date.now() - startedAt.current < 3000) {
      toast.error("Please take a moment to fill the form.");
      return;
    }
    const r = inquirySchema.safeParse(form);
    if (!r.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of r.error.issues) {
        const key = issue.path[0] as string;
        if (key && !fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      toast.error(r.error.issues[0]?.message ?? "Please complete the form");
      const firstKey = r.error.issues[0]?.path[0] as string | undefined;
      if (firstKey) {
        const el = document.getElementById(firstKey);
        (el as HTMLInputElement | null)?.focus();
      }
      return;
    }
    setErrors({});

    setSubmitting(true);
    const { error } = await supabase.from("inquiry_submissions").insert({
      lane: form.lane,
      full_name: form.full_name.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim() || null,
      organization: form.organization.trim() || null,
      role: form.role.trim() || null,
      event_or_focus: form.event_or_focus.trim() || null,
      date_or_timeline: form.date_or_timeline.trim() || null,
      budget_range: form.budget_range.trim() || null,
      message: form.message.trim(),
      source: inquireCtx.source
        ? `drsullyspeaks-native:${inquireCtx.source}`
        : "drsullyspeaks-native",
    });
    setSubmitting(false);

    if (error) {
      console.error("inquiry_submissions insert error:", error);
      toast.error("Something went wrong. Please try again.");
      return;
    }
    // Fire conversion events so /funnel + GA4 can measure strategy-session
    // and masterclass-lane inquiries independently.
    trackConversion("inquiry_submit", {
      lane: form.lane,
      has_org: !!form.organization,
      has_budget: !!form.budget_range,
      ...ctxProps,
    });
    trackConversion("strategy_session_submit", { lane: form.lane, ...ctxProps });
    if (form.lane === "masterclass") {
      trackConversion("masterclass_conversion", { source: "strategy_form", ...ctxProps });
    }
    trackCTA("inquire_confirmation_view", {
      surface: "strategy_confirmation",
      lane: form.lane,
      ...ctxProps,
    });
    setDone(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageMeta
        title="Inquire — Work With Dr. Sully"
        description="Send an inquiry for executive coaching, keynote speaking, or organizational consulting. One business-day response."
        path="/inquire"
      />
      <SiteNav />

      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-hero overflow-hidden">
        <div className="container max-w-3xl relative">
          <Reveal variant="up" className="text-center">
            <span className="eyebrow !mx-auto">Inquiry</span>
            <h1 className="font-display font-bold uppercase text-white text-4xl md:text-6xl mt-4 leading-[1.05]">
              {done ? <>Inquiry <span className="text-gradient-gold">received.</span></> : <>Tell us the <span className="text-gradient-gold">work.</span></>}
            </h1>
            {!done && (
              <p className="text-silver text-base md:text-lg mt-5 max-w-xl mx-auto leading-relaxed">
                Apply for a strategy session. The more specific you are, the faster we can route you to the right next step.
              </p>
            )}
          </Reveal>

          {!done && (
            <Reveal variant="fade" delay={120} className="mt-8 flex flex-wrap items-center justify-center gap-3 md:gap-5">
              {TRUST.map(({ icon: Icon, label }) => (
                <div key={label} className="inline-flex items-center gap-2 border border-gold/30 bg-navy-elevated/50 px-3 py-1.5 rounded-sm">
                  <Icon className="h-3.5 w-3.5 text-gold" />
                  <span className="text-[11px] uppercase tracking-[0.2em] text-silver">{label}</span>
                </div>
              ))}
            </Reveal>
          )}
        </div>
      </section>

      <section className="pb-24">
        <div className="container max-w-3xl">
          {done ? (
            <Reveal variant="scale" className="premium-card p-8 md:p-12 text-center">
              <div
                data-inquire-lane={form.lane}
                data-inquire-tier={inquireCtx.tier || "unset"}
                data-inquire-source={inquireCtx.source || "direct"}
                data-inquire-intent={inquireCtx.intent || "none"}
              >
                <div className="relative inline-flex">
                  <div className="absolute inset-0 bg-gold/30 blur-2xl rounded-full" />
                  <CheckCircle2 className="relative h-16 w-16 text-gold mx-auto" />
                </div>
                <h2 className="font-display uppercase text-white text-2xl md:text-3xl mt-6">
                  You're <span className="text-gradient-gold">in the queue.</span>
                </h2>
                <p className="text-silver text-base mt-5 max-w-md mx-auto leading-relaxed">
                  Dr. Sully's team reviews every inquiry within one business day. You'll hear back at <span className="text-white">{form.email}</span> with next steps.
                </p>
                {inquireCtx.tier && (
                  <p className="text-[11px] uppercase tracking-[0.2em] text-gold mt-4">
                    Logged for the {inquireCtx.tier} tier
                  </p>
                )}
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                  <Link
                    to={buildInquireHref({
                      path: "/scan",
                      lane: form.lane,
                      tier: inquireCtx.tier,
                      source: "inquire_confirmation",
                    })}
                    onClick={() =>
                      trackCTA("inquire_confirmation_scan_click", {
                        surface: "strategy_confirmation",
                        lane: form.lane,
                        ...ctxProps,
                      })
                    }
                    className="inline-flex items-center justify-center gap-2 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 shadow-cta hover:brightness-110 transition-all"
                  >
                    Take the Free Scan While You Wait
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                  <Link to="/" className="inline-flex items-center justify-center gap-2 border border-gold/40 text-white font-display uppercase tracking-[0.2em] text-xs px-6 py-4 hover:bg-gold/10 transition-all">
                    Back to Home
                  </Link>
                </div>
              </div>
            </Reveal>

          ) : (
            <Reveal variant="scale" className="premium-card p-6 md:p-10">
              <form onSubmit={handleSubmit} onFocusCapture={heroFormStart} className="space-y-7" noValidate>
                {/* Honeypot */}
                <div className="absolute -left-[9999px] opacity-0" aria-hidden>
                  <Label htmlFor="website">Website</Label>
                  <input id="website" type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={update("website")} />
                </div>

                {prefilledCount > 0 && (
                  <div
                    role="status"
                    className="border border-gold/40 bg-gold/5 px-4 py-3 text-xs text-silver leading-relaxed"
                  >
                    <span className="text-gold font-display uppercase tracking-[0.2em] text-[10px] block mb-1">
                      Pre-filled from your selection
                    </span>
                    {isTierInquiry
                      ? `We carried over your details for the ${prefill.tier} tier. Review, adjust anything, and submit — no retyping needed.`
                      : "We carried over the details you already shared. Review, adjust anything, and submit."}
                  </div>
                )}



                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-gold mb-4">What are you looking for?</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {LANES.map((l) => (
                      <button
                        key={l.value}
                        type="button"
                        onClick={() => {
                          setForm((s) => ({ ...s, lane: l.value }));
                          trackCTA("strategy_lane_select", { lane: l.value, surface: "strategy_form" });
                        }}
                        className={cn(
                          "p-3 text-left transition-all border",
                          form.lane === l.value
                            ? "bg-navy-elevated border-orange shadow-[0_0_20px_-6px_hsl(var(--orange)/0.5)]"
                            : "bg-navy hover:bg-navy-elevated border-border hover:border-gold/40",
                        )}
                      >
                        <div className={cn("text-[10px] uppercase tracking-[0.2em] leading-tight", form.lane === l.value ? "text-orange" : "text-gold")}>
                          {l.label}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="gold-divider opacity-30" />

                <div className="grid md:grid-cols-2 gap-5">
                  <Field label="Full Name *" id="full_name" error={errors.full_name}>
                    <Input
                      id="full_name"
                      className={cn(inputCls, errors.full_name && "border-red-500/70 focus-visible:ring-red-500/60 focus-visible:border-red-500/70")}
                      value={form.full_name}
                      onChange={update("full_name")}
                      onBlur={() => validateField("full_name")}
                      aria-invalid={!!errors.full_name}
                      aria-describedby={errors.full_name ? "full_name-error" : undefined}
                      maxLength={100}
                      required
                    />
                  </Field>
                  <Field label="Email *" id="email" error={errors.email}>
                    <Input
                      id="email"
                      type="email"
                      className={cn(inputCls, errors.email && "border-red-500/70 focus-visible:ring-red-500/60 focus-visible:border-red-500/70")}
                      value={form.email}
                      onChange={update("email")}
                      onBlur={() => validateField("email")}
                      aria-invalid={!!errors.email}
                      aria-describedby={errors.email ? "email-error" : undefined}
                      maxLength={255}
                      required
                    />
                  </Field>
                  <Field label="Phone" id="phone">
                    <Input id="phone" type="tel" className={inputCls} value={form.phone} onChange={update("phone")} maxLength={32} placeholder="(optional)" />
                  </Field>
                  <Field label="Organization" id="organization">
                    <Input id="organization" className={inputCls} value={form.organization} onChange={update("organization")} maxLength={150} />
                  </Field>
                  <Field label="Your Role" id="role">
                    <Input id="role" className={inputCls} value={form.role} onChange={update("role")} maxLength={100} placeholder="CEO, Founder, Pastor..." />
                  </Field>
                  <Field label="Timeline / Date" id="date_or_timeline">
                    <Input id="date_or_timeline" className={inputCls} value={form.date_or_timeline} onChange={update("date_or_timeline")} maxLength={200} placeholder="Q1 2026, ASAP, flexible..." />
                  </Field>
                </div>

                <Field label="Event or Focus Area" id="event_or_focus">
                  <Input id="event_or_focus" className={inputCls} value={form.event_or_focus} onChange={update("event_or_focus")} maxLength={500} placeholder="Annual leadership summit, exec coaching for me, board strategy..." />
                </Field>

                <Field label="Budget Range" id="budget_range">
                  <Input id="budget_range" className={inputCls} value={form.budget_range} onChange={update("budget_range")} maxLength={100} placeholder="$5K–$15K, $15K–$50K, $50K+..." />
                </Field>

                <Field label="Tell us about the work *" id="message" error={errors.message}>
                  <Textarea
                    id="message"
                    rows={5}
                    className={cn(inputCls, errors.message && "border-red-500/70 focus-visible:ring-red-500/60 focus-visible:border-red-500/70")}
                    value={form.message}
                    onChange={update("message")}
                    onBlur={() => validateField("message")}
                    aria-invalid={!!errors.message}
                    aria-describedby={errors.message ? "message-error" : undefined}
                    maxLength={2000}
                    placeholder="What you're building, who you serve, what success looks like in 90 days."
                    required
                  />
                </Field>


                <Button type="submit" disabled={submitting} className="w-full bg-cta hover:bg-cta hover:brightness-110 text-white font-display uppercase tracking-[0.2em] py-6 shadow-cta transition-all">
                  {submitting ? "Sending..." : "Submit Inquiry"}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>

                <p className="text-[10px] uppercase tracking-[0.2em] text-silver/60 text-center">
                  Replies sent from DrSully@DrSullySpeaks.com · Confidential · No spam.
                </p>
              </form>
            </Reveal>
          )}

          <div className="mt-10 text-center">
            <Link to="/" className="text-silver text-xs uppercase tracking-[0.25em] hover:text-white inline-flex items-center gap-2">
              <ArrowLeft className="h-3 w-3" /> Back to home
            </Link>
          </div>
        </div>
      </section>

      {!done && (
        <ConversionBand
          eyebrow="Not Ready Yet?"
          heading="Take the free Scan first."
          subheading="A 2-minute diagnostic that maps your expertise into a fundable program lane — delivered to your inbox as a personalized PDF."
          primaryLabel="Take the Scan"
          primaryHref="/scan"
          secondaryLabel="See Pricing"
          secondaryHref="/invest"
        />
      )}

      <SiteFooter />
    </div>
  );
};

const Field = ({
  label,
  id,
  children,
  error,
}: {
  label: string;
  id: string;
  children: React.ReactNode;
  error?: string;
}) => (
  <div>
    <Label htmlFor={id} className="text-xs uppercase tracking-[0.25em] text-gold">
      {label}
    </Label>
    <div className="mt-2">{children}</div>
    {error && (
      <p id={`${id}-error`} role="alert" className="text-xs text-red-400 mt-1.5">
        {error}
      </p>
    )}
  </div>
);

export default Strategy;
